import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { Button } from '../ui/button'

export interface ProductBlankStateProps {
  /** The product's name, as the page heading. */
  title: string
  heading: string
  lines?: string[]
  cta?: { label: string; href: string }
  /** Drawn in place of the button, e.g. app store badges. */
  actions?: ReactNode
  learnMore?: { label: string; href: string }
  /** What the product shows off, in place of the grey placeholder. */
  preview?: ReactNode
}

/** A product the fleet has not turned on: the tab is there, and the page is an offer. */
export function ProductBlankState({
  title,
  heading,
  lines = [],
  cta,
  actions,
  learnMore,
  preview,
}: ProductBlankStateProps) {
  return (
    <div className="flex flex-1 flex-col gap-4">
      <h1 className="text-ink text-2xl font-bold tracking-[-0.01em]">{title}</h1>
      <div className="border-ink-100 flex flex-1 flex-col items-center justify-center gap-3 rounded-md border border-dashed p-6">
        {/* One fixed frame for every preview, so the copy below sits in the same place on every tab. */}
        <div className="flex h-72 w-full max-w-[420px] items-center justify-center overflow-hidden">
          {preview ?? <div className="bg-ink-50 size-full rounded-md" aria-hidden="true" />}
        </div>
        <div className="flex max-w-md flex-col items-center gap-1 text-center">
          <p className="text-ink text-xl font-semibold">{heading}</p>
          {lines.map((line) => (
            <p key={line} className="text-md text-ink-500 leading-relaxed">
              {line}
            </p>
          ))}
        </div>
        {actions ??
          (cta && (
            <Button asChild>
              {/* A page in this app opens in place; another app's loads anew. */}
              {cta.href.startsWith('/') ? (
                <Link to={cta.href}>{cta.label}</Link>
              ) : (
                <a href={cta.href}>{cta.label}</a>
              )}
            </Button>
          ))}
        {learnMore && (
          <Button asChild variant="link">
            <a href={learnMore.href} target="_blank" rel="noopener noreferrer">
              {learnMore.label}
            </a>
          </Button>
        )}
      </div>
    </div>
  )
}
